import { useEffect, useState } from 'react';
import {
  isSignInWithEmailLink,
  onAuthStateChanged,
  sendSignInLinkToEmail,
  signInWithEmailLink,
  signOut as fbSignOut,
  type User,
} from 'firebase/auth';
import { auth } from '../firebase';

const STORAGE_KEY = 'hangesten:emailForSignIn';

export interface AuthState {
  user: User | null;
  isAdmin: boolean;
  loading: boolean;
}

export function useAuth(): AuthState {
  const [state, setState] = useState<AuthState>({
    user: null,
    isAdmin: false,
    loading: true,
  });

  useEffect(() => {
    let cancelled = false;
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        if (!cancelled) {
          setState({ user: null, isAdmin: false, loading: false });
        }
        return;
      }
      const tokenResult = await user.getIdTokenResult();
      if (cancelled || auth.currentUser?.uid !== user.uid) return;
      setState({
        user,
        isAdmin: tokenResult.claims.admin === true,
        loading: false,
      });
    });
    return () => {
      cancelled = true;
      unsub();
    };
  }, []);

  return state;
}

export async function sendMagicLink(email: string): Promise<void> {
  const url = `${window.location.origin}/login/callback`;
  await sendSignInLinkToEmail(auth, email, {
    url,
    handleCodeInApp: true,
  });
  window.localStorage.setItem(STORAGE_KEY, email);
}

export async function completeMagicLink(href: string): Promise<User> {
  if (!isSignInWithEmailLink(auth, href)) {
    throw new Error('not-a-magic-link');
  }
  let email = window.localStorage.getItem(STORAGE_KEY);
  if (!email) {
    email = window.prompt('Bekräfta din e-postadress för att logga in:') ?? '';
  }
  if (!email) throw new Error('email-missing');
  const result = await signInWithEmailLink(auth, email, href);
  window.localStorage.removeItem(STORAGE_KEY);
  return result.user;
}

export function signOut(): Promise<void> {
  return fbSignOut(auth);
}
